import { Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import cn from "classnames";
import types from "constants/types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Tour from "reactour";
import Flex from "../../../../components/Flex";
import { selectGuideListByKey } from "../../../../stores/config/config.selector";
import {
  getIsGuideByKey,
  GUIDE_TYPE_ENUM,
  setIsGuideByKey
} from "../../../../utils/helper";
import styles from "../../styles.module.scss"; // Ensure you have this file
import BombotTagChart from "./BombotTagChart";
import CampaignChart from "./CampaignChart";
import CustomerChart from "./CustomerChart";
import CustomerOverview from "./CustomerOverview";
import FanpageChart from "./FanpageChart";
import FBTagChart from "./FanpageTagChart";
import GenAIChart from "./GenAIChart";
import MessageChart from "./MessageChart";
import ScenarioChart from "./ScenarioChart";
import { Skeleton } from "@mui/material";

const useStyles = makeStyles({
  button_try_it_now: {
    position: "relative",
    margin: 0,
    background: "#05AE34",
    fontSize: 14,
    fontWeight: "bold",
    color: "white",
    transition: "transform 0.3s, box-shadow 0.3s",
    "&:hover": {
      transform: "scale(1.1)",
      boxShadow: "0 0 10px rgba(0, 255, 89, 0.5)"
    }
  },
  stars: {
    position: "absolute",
    top: "-10%",
    right: "10%",
    transform: "translate(-50%, -50%)",
    opacity: 0,
    transition: "opacity 0.3s ease",
    pointerEvents: "none"
  },
  star: {
    position: "absolute",
    fontSize: "18px",
    animation: "$twinkle 1s infinite"
  },
  star1: { top: "0", left: "0", animationDelay: "0.02s" },
  button_try_it_now_hover: {
    "&:hover $stars": {
      opacity: 1
    }
  },
  "@keyframes twinkle": {
    "0%": { transform: "scale(1)" },
    "50%": { transform: "scale(1.2)" },
    "100%": { transform: "scale(1)" }
  }
});

const DashboardChart = ({
  dashboard,
  campaigns,
  messages,
  scenarios,
  selectedDate = null,
  onDateChange,
  isShowTooltip
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const dataCampaigns = {
    labels: [
      "Campaign Running",
      "Campaign Pending",
      "Campaign Done",
      "Campaign Cancelled"
    ],
    datasets: [
      {
        data: [
          dashboard?.campaign?.totalCampaignRunning || 0,
          dashboard?.campaign?.totalCampaignPending || 0,
          dashboard?.campaign?.totalCampaignDone || 0,
          dashboard?.campaign?.totalCampaignCancel || 0
        ],
        backgroundColor: ["#82ca9d", "#ffc658", "#8884d8", "#AE1607"],
        hoverBackgroundColor: ["#76b89a", "#e6b84d", "#7a6fb8", "#9e1410"]
      }
    ]
  };

  const dataMessages = {
    labels: ["Message", "Message Fail", "Message Not Send", "Message Success"],
    datasets: [
      {
        data: [
          messages?.totalMessages || 0,
          messages?.totalMessagesFail || 0,
          messages?.totalMessagesNotSend || 0,
          messages?.totalMessagesSuccess || 0
        ],
        backgroundColor: ["#82ca9d", "#ffc658", "#8884d8", "#AE1607"],
        hoverBackgroundColor: ["#76b89a", "#e6b84d", "#7a6fb8", "#9e1410"]
      }
    ]
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top"
      }
    }
  };

  const tourOverview = useSelector(
    selectGuideListByKey(GUIDE_TYPE_ENUM.dashboard)
  );

  const [tourConfig, setTourConfig] = useState(null);

  useEffect(() => {
    if (
      getIsGuideByKey(GUIDE_TYPE_ENUM.dashboard) &&
      tourOverview?.length &&
      isShowTooltip
    ) {
      setTourConfig(tourOverview);
      setIsGuideByKey(GUIDE_TYPE_ENUM.dashboard);
    }
  }, [tourOverview, isShowTooltip]);

  // link qua ext
  const handleTryItNow = () => {
    console.log("thieu truyen param show popup guid");
    window.open(
      types.URL.FACEBOOK +
        `&token=${
          localStorage.getItem("token") || null
        }&guidBombotGenAI=guidBombotGenAI`,
      "_blank"
    );
  };

  // Check if dashboard has data
  if (!dashboard || !dashboard.fanpage || !campaigns) {
    return (
      <Flex
        style={{
          width: "100%",
          flexWrap: "wrap",
          gap: "20px",
          marginTop: "22px"
        }}
        justify="space-between"
        align="stretch"
      >
        <div
          style={{
            flex: "1 1 calc(50% - 20px)",
            padding: "20px",
            borderRadius: "8px",
            overflow: "hidden",
            border: "1px solid #efefef",
            backgroundColor: "#fff",
            minHeight: "250px"
          }}
        >
          <Skeleton variant="rectangular" width="100%" height="100%" />
        </div>

        <div
          style={{
            flex: "1 1 calc(50% - 20px)",
            padding: "20px",
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: "#fff",
            border: "1px solid #efefef",
            minHeight: "250px"
          }}
        >
          <Skeleton variant="rectangular" width="100%" height="100%" />
        </div>
      </Flex>
    );
  }

  return (
    <Flex
      align="center"
      justify="space-between"
      flexWrap="wrap"
      row
      className={styles.box}
      style={{
        padding: "15px 0"
      }}
    >
      <Tour
        steps={tourConfig}
        isOpen={tourConfig != null}
        closeWithMask={false}
        lastStepNextButton={
          <Button size="small" className="btn--rounder" variant="contained">
            {t("common.btn.close")}
          </Button>
        }
        accentColor="#05ae34"
        onRequestClose={() => setTourConfig(null)}
      />

      <Flex
        style={{
          width: "100%",
          flexWrap: "wrap",
          gap: "20px"
        }}
        justify="space-between"
        align="stretch"
      >
        <CampaignChart
          id="camp_chart"
          style={{
            flex: "1 1 calc(50% - 20px)",
            padding: "20px",
            borderRadius: "12px",
            overflow: "hidden",
            backgroundColor: "#fff",
            minHeight: "250px"
          }}
        />

        <ScenarioChart
          id="scenario_chart"
          style={{
            flex: "1 1 calc(50% - 20px)",
            padding: "20px",
            borderRadius: "12px",
            overflow: "hidden",
            backgroundColor: "#fff",
            minHeight: "250px"
          }}
        />
      </Flex>

      <Flex
        style={{
          width: "100%",
          flexWrap: "wrap",
          gap: "20px",
          marginTop: "20px"
        }}
        justify="space-between"
        align="stretch"
      >
        <CustomerChart
          id="customer_chart"
          style={{
            flex: "1 1 calc(50% - 20px)",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
            backgroundColor: "#fff",
            minHeight: "250px"
          }}
        />
        <MessageChart
          id="message_chart"
          style={{
            flex: "1 1 calc(50% - 20px)", // 50% width minus gap
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
            backgroundColor: "#fff",
            minHeight: "250px"
          }}
        />
      </Flex>

      <Flex
        style={{
          width: "100%",
          flexWrap: "wrap",
          gap: "20px",
          marginTop: "20px"
        }}
        justify="space-between"
        align="stretch"
      >
        <GenAIChart
          id="genAI_chart"
          style={{
            flex: "1 1 calc(50% - 20px)",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
            backgroundColor: "#fff",
            minHeight: "250px"
          }}
        />
        <div
          style={{
            backgroundColor: "white",
            flex: "1 1 calc(50% - 20px)",
            paddingTop: "20px",
            paddingLeft: "20px",
            paddingRight: "20px",
            borderRadius: "12px",
            border: "1px solid #efefef",
            overflow: "hidden",
            minHeight: "250px",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            color: "#333"
          }}
        >
          <div>
            <div style={{ fontSize: "13px", color: "#05AE34" }}>
              {t("common.btn.new_feature")}
            </div>
            <div
              style={{
                fontSize: "18px",
                color: "#05AE34",
                fontWeight: "bold",
                marginBottom: "20px"
              }}
            >
              BOMBOT GENAI
            </div>
            <Button
              style={{
                background: "#05AE34",
                fontSize: 14,
                fontWeight: "bold",
                color: "white"
              }}
              className={cn(
                classes.button_try_it_now,
                classes.button_try_it_now_hover
              )}
              variant="contained"
              onClick={() => handleTryItNow()}
            >
              {t("common.btn.try_it_now")}
              <div className={classes.stars}>
                <span className={cn(classes.star, classes.star1)}>✨</span>
              </div>
            </Button>
          </div>

          <div
            style={{
              flex: 1,
              display: "flex",
              justifyContent: "right",
              alignItems: "center",
              overflow: "hidden",
              maxHeight: "calc(250px - 20px)"
            }}
          >
            <img
              src="/image/Group32.svg"
              alt="bombot-2"
              style={{
                backgroundColor: "white",
                paddingTop: "80px",
                maxWidth: "68%",
                maxHeight: "100%",
                objectFit: "contain"
              }}
            />
          </div>
        </div>
      </Flex>

      <CustomerOverview id="customer_stat_chart" />

      <FanpageChart
        id="page_cus_chart"
        style={{
          borderRadius: "12px",
          width: "100%",
          padding: "16px",
          marginBottom: "20px"
        }}
      />

      <BombotTagChart
        id="tag_bombot_cus_chart"
        style={{
          borderRadius: "12px",
          width: "100%",
          padding: "16px",
          marginBottom: "20px"
        }}
      />

      <FBTagChart
        id="tag_fb_cus_chart"
        style={{
          borderRadius: "12px",
          width: "100%",
          padding: "16px"
        }}
      />
    </Flex>
  );
};

export default DashboardChart;
